(function () {
  const registry = new WeakMap();
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class HanfiHeroSlideshow {
    constructor(root) {
      this.root = root;
      this.carousel = root.querySelector('.hanfi-hero-slide__carousel');
      this.slides = [...root.querySelectorAll('[data-hanfi-hero-slide]')];
      this.dots = [...root.querySelectorAll('[data-hanfi-hero-dot]')];
      this.prev = root.querySelector('[data-hanfi-hero-prev]');
      this.next = root.querySelector('[data-hanfi-hero-next]');
      this.autoplay = root.dataset.autoplay === 'true';
      this.interval = Number(root.dataset.interval || 5000);
      this.activeIndex = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.timer = null;
      this.transitionTimer = null;
      this.isAnimating = false;
      this.isInViewport = true;
      this.touchStartX = 0;

      this.handlePrev = this.handlePrev.bind(this);
      this.handleNext = this.handleNext.bind(this);
      this.stop = this.stop.bind(this);
      this.start = this.start.bind(this);
      this.handleVisibility = this.handleVisibility.bind(this);
      this.handleTouchStart = this.handleTouchStart.bind(this);
      this.handleTouchEnd = this.handleTouchEnd.bind(this);

      this.attach();
      this.goTo(this.activeIndex, { animate: false, restart: false });
      this.decodeAdjacent();
      this.observeViewport();
      this.start();
    }

    attach() {
      this.prev?.addEventListener('click', this.handlePrev);
      this.next?.addEventListener('click', this.handleNext);
      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.goTo(index, { direction: index > this.activeIndex ? 'next' : 'prev' }));
      });
      this.root.addEventListener('mouseenter', this.stop, { passive: true });
      this.root.addEventListener('mouseleave', this.start, { passive: true });
      this.root.addEventListener('focusin', this.stop, { passive: true });
      this.root.addEventListener('focusout', this.start, { passive: true });
      this.root.addEventListener('touchstart', this.handleTouchStart, { passive: true });
      this.root.addEventListener('touchend', this.handleTouchEnd, { passive: true });
      document.addEventListener('visibilitychange', this.handleVisibility);
    }

    destroy() {
      this.stop();
      window.clearTimeout(this.transitionTimer);
      this.prev?.removeEventListener('click', this.handlePrev);
      this.next?.removeEventListener('click', this.handleNext);
      this.root.removeEventListener('mouseenter', this.stop);
      this.root.removeEventListener('mouseleave', this.start);
      this.root.removeEventListener('focusin', this.stop);
      this.root.removeEventListener('focusout', this.start);
      this.root.removeEventListener('touchstart', this.handleTouchStart);
      this.root.removeEventListener('touchend', this.handleTouchEnd);
      document.removeEventListener('visibilitychange', this.handleVisibility);
      this.viewportObserver?.disconnect();
    }

    observeViewport() {
      if (!('IntersectionObserver' in window)) return;
      this.viewportObserver = new IntersectionObserver(
        ([entry]) => {
          this.isInViewport = Boolean(entry?.isIntersecting);
          this.isInViewport ? this.start() : this.stop();
        },
        { rootMargin: '160px 0px', threshold: 0.01 }
      );
      this.viewportObserver.observe(this.root);
    }

    handlePrev() {
      this.goTo(this.activeIndex - 1, { direction: 'prev' });
    }

    handleNext() {
      this.goTo(this.activeIndex + 1, { direction: 'next' });
    }

    handleVisibility() {
      document.hidden ? this.stop() : this.start();
    }

    handleTouchStart(event) {
      this.touchStartX = event.changedTouches?.[0]?.clientX || 0;
    }

    handleTouchEnd(event) {
      const endX = event.changedTouches?.[0]?.clientX || 0;
      const delta = endX - this.touchStartX;
      if (Math.abs(delta) < 42) return;
      delta > 0 ? this.handlePrev() : this.handleNext();
    }

    start() {
      if (!this.autoplay || this.slides.length < 2 || document.hidden || !this.isInViewport) return;
      this.stop();
      this.timer = window.setInterval(() => this.goTo(this.activeIndex + 1, { direction: 'next', restart: false }), this.interval);
    }

    stop() {
      if (!this.timer) return;
      window.clearInterval(this.timer);
      this.timer = null;
    }

    normalize(index) {
      return (index + this.slides.length) % this.slides.length;
    }

    goTo(index, options = {}) {
      if (!this.slides.length) return;

      const nextIndex = this.normalize(index);
      const currentIndex = this.activeIndex;
      const currentSlide = this.slides[currentIndex];
      const nextSlide = this.slides[nextIndex];
      const direction = options.direction || (nextIndex > currentIndex ? 'next' : 'prev');
      const animate = options.animate !== false && !prefersReducedMotion.matches;

      if (!nextSlide || (nextIndex === currentIndex && options.animate !== false)) return;
      if (this.isAnimating) return;

      this.activeIndex = nextIndex;
      this.updateDots();

      if (!animate || !currentSlide || nextSlide === currentSlide) {
        this.slides.forEach((slide, slideIndex) => {
          const active = slideIndex === nextIndex;
          slide.classList.toggle('is-active', active);
          slide.classList.remove('is-exiting', 'is-preparing');
          slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        });
        this.carousel?.classList.remove('is-transitioning');
        this.decodeAdjacent();
        if (options.restart !== false) this.start();
        return;
      }

      this.isAnimating = true;
      this.stop();

      const incoming = direction === 'prev' ? '-100%' : '100%';
      const outgoing = direction === 'prev' ? '100%' : '-100%';
      this.carousel?.style.setProperty('--hanfi-hero-incoming', incoming);
      this.carousel?.style.setProperty('--hanfi-hero-outgoing', outgoing);
      this.carousel?.classList.remove('is-transitioning');

      nextSlide.classList.add('is-preparing');
      nextSlide.setAttribute('aria-hidden', 'false');

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.carousel?.classList.add('is-transitioning');
          currentSlide.classList.remove('is-active');
          currentSlide.classList.add('is-exiting');
          currentSlide.setAttribute('aria-hidden', 'true');
          nextSlide.classList.remove('is-preparing');
          nextSlide.classList.add('is-active');

          const finish = () => {
            currentSlide.classList.remove('is-exiting');
            this.carousel?.classList.remove('is-transitioning');
            this.isAnimating = false;
            this.decodeAdjacent();
            if (options.restart !== false) this.start();
          };

          window.clearTimeout(this.transitionTimer);
          this.transitionTimer = window.setTimeout(finish, 760);
        });
      });
    }

    updateDots() {
      this.dots.forEach((dot, index) => {
        const active = index === this.activeIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
    }

    decodeAdjacent() {
      if (!this.slides.length) return;
      const indexes = [this.activeIndex, this.normalize(this.activeIndex + 1), this.normalize(this.activeIndex - 1)];
      indexes.forEach((index) => {
        const image = this.slides[index]?.querySelector('img');
        if (!image) return;
        image.loading = index === this.activeIndex ? 'eager' : 'lazy';
        if ('decode' in image && image.complete) {
          image.decode().catch(() => {});
        }
      });
    }

    goToBlock(blockId) {
      const index = this.slides.findIndex((slide) => slide.dataset.slideBlockId === blockId);
      if (index >= 0) {
        this.goTo(index, { animate: false, restart: false });
        this.stop();
      }
    }
  }

  function init(container = document) {
    container.querySelectorAll?.('[data-hanfi-hero-slideshow]').forEach((root) => {
      if (registry.has(root)) return;
      registry.set(root, new HanfiHeroSlideshow(root));
    });
  }

  function destroy(container = document) {
    container.querySelectorAll?.('[data-hanfi-hero-slideshow]').forEach((root) => {
      registry.get(root)?.destroy();
      registry.delete(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest?.('[data-slide-block-id]');
    const root = event.target.closest?.('[data-hanfi-hero-slideshow]');
    if (!slide || !root) return;
    registry.get(root)?.goToBlock(slide.dataset.slideBlockId);
  });
})();
